import { useCallback, useMemo, useState } from 'react'

export type RowSelection<T> = {
  selectedIds: string[]
  selectedRows: T[]
  count: number
  isSelected: (id: string) => boolean
  toggle: (id: string) => void
  toggleAll: () => void
  clear: () => void
  allSelected: boolean
  someSelected: boolean
}

export function useRowSelection<T>(rows: T[], getId: (row: T) => string): RowSelection<T> {
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  // Prune ids that have left the filtered set so a stale selection can never be submitted.
  const visibleIds = useMemo(() => rows.map(getId), [rows, getId])
  const liveSelected = useMemo(
    () => selectedIds.filter((id) => visibleIds.includes(id)),
    [selectedIds, visibleIds],
  )

  const isSelected = useCallback((id: string) => liveSelected.includes(id), [liveSelected])
  const toggle = useCallback((id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]))
  }, [])
  const toggleAll = useCallback(() => {
    setSelectedIds((prev) => (visibleIds.every((id) => prev.includes(id)) ? [] : visibleIds))
  }, [visibleIds])
  const clear = useCallback(() => setSelectedIds([]), [])

  const selectedRows = useMemo(
    () => rows.filter((r) => liveSelected.includes(getId(r))),
    [rows, liveSelected, getId],
  )

  return {
    selectedIds: liveSelected,
    selectedRows,
    count: liveSelected.length,
    isSelected,
    toggle,
    toggleAll,
    clear,
    allSelected: visibleIds.length > 0 && visibleIds.every((id) => liveSelected.includes(id)),
    someSelected: liveSelected.length > 0 && liveSelected.length < visibleIds.length,
  }
}
